import LocalizedLink from '@components/Localization/LocalizedLink'
import { Page, pickSeoProps } from '@components/Page'
import { RCPOBannerWithTitle } from '@components/_rcpo/BannerWithTitle'
import { Breadcrumbs } from '@components/_rcpo/Breadcrumbs'
import CheckerboardItem from '@components/_rcpo/CheckerboardItem'
import ImageBanner from '@components/_rcpo/ImageBanner'
import { RCPOPage } from '@components/_rcpo/Page'
import KeepExploring from '@components/_rcpo/Sliders/KeepExploring'
import TextBlock from '@components/_rcpo/TextBlock'
import { Container, Image } from '@components/gassan-ui'
import { Button } from '@components/old-rolex-ui'
import { buildLanguageAlternateNavigation } from '@lib/build-language-alternates'
import { cn } from '@lib/cn'
import { getServerSideTranslations } from '@lib/get-server-side-translations'
import { SingleItemResponse, getKontentImage, getPageByCodename } from '@lib/kontent'
import { DeletedRolexRcpoCertificationPage } from '@lib/kontent/models/DeletedRolexRcpoCertificationPage'
import { RolexCheckerboardItem } from '@lib/kontent/models/RolexCheckerboardItem'
import { GetStaticProps, NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

type RCPOCertificationProps = SingleItemResponse<DeletedRolexRcpoCertificationPage>

const RCPOCertification: NextPage<RCPOCertificationProps> = ({ item, modular_content }) => {
  const { t } = useTranslation()
  const router = useRouter()
  const checkerboardItems = item.elements.checkerboard_items.value.map(
    (key) => modular_content[key].elements,
  ) as RolexCheckerboardItem[]
  const keepExploringItems = item.elements.keep_exploring_items.value.map(
    (key) => modular_content[key].elements,
  )
  return (
    <Page
      {...pickSeoProps(item.elements)}
      languageAlternates={buildLanguageAlternateNavigation(
        router.locales,
        'rcpo.certification.url',
      )}
    >
      <RCPOPage pageType="certification page">
        <Breadcrumbs
          items={['index']}
          currentPageTitle={t('rcpo.certification.title', { ns: 'navigation' })}
        />
        <ImageBanner
          desktopImage={item.elements.banner_desktop_image}
          mobileImage={item.elements.banner_mobile_image}
          className="mb-[5vh] mt-0"
        />
        <TextBlock
          headingTag="h1"
          title={item.elements.intro_title.value}
          description={item.elements.intro_description.value}
        />
        <Container className="my-8 !max-w-[1140px] md:my-16">
          <Image
            className={cn('aspect-[20/19]', 'md:aspect-[2/1]')}
            alt={item.elements.intro_desktop_image.value[0].description || ''}
            sources={[
              getKontentImage(item.elements.intro_mobile_image.value[0].url, { width: 600 }),
              getKontentImage(item.elements.intro_desktop_image.value[0].url, { width: 1024 }),
              getKontentImage(item.elements.intro_desktop_image.value[0].url, { width: 1200 }),
            ]}
            placeholder={getKontentImage(item.elements.intro_mobile_image.value[0].url, {
              width: 30,
            })}
          />
        </Container>
        <TextBlock
          className="py-8 sm:py-16"
          headingTag="h2"
          title={item.elements.authenticity_title.value}
          description={item.elements.authenticity_description.value}
        />
        {checkerboardItems.slice(0, 3).map((item, key) => (
          <CheckerboardItem
            key={key}
            desktopImage={item.desktop_image}
            mobileImage={item.mobile_image}
            title={item.title.value}
            description={item.description.value}
            variant={key % 2 ? 'text-image' : 'image-text'}
            buttonTitle={item.button_title.value}
            buttonUrl={item.button_url.value}
          />
        ))}
        <RCPOBannerWithTitle
          title={item.elements.second_banner_title.value}
          mobileImage={item.elements.second_banner_mobile_image}
          desktopImage={item.elements.second_banner_desktop_image}
        />
        <Container className="mt-8 !max-w-[1140px] md:mt-16">
          <Image
            className={cn('aspect-[20/19]', 'md:aspect-[2/1]')}
            alt={item.elements.buying_desktop_image.value[0].description || ''}
            sources={[
              getKontentImage(item.elements.buying_mobile_image.value[0].url, { width: 600 }),
              getKontentImage(item.elements.buying_desktop_image.value[0].url, { width: 1024 }),
              getKontentImage(item.elements.buying_desktop_image.value[0].url, { width: 1200 }),
            ]}
            placeholder={getKontentImage(item.elements.buying_mobile_image.value[0].url, {
              width: 30,
            })}
          />
        </Container>
        <TextBlock
          className="py-8 sm:py-16"
          headingTag="h2"
          title={item.elements.buying_title.value}
          description={item.elements.buying_description.value}
        >
          <LocalizedLink href={t('rcpo.contact.url', { ns: 'navigation' })}>
            <Button as="link" variant="primary" className="mt-8">
              {t('navigation.contactUs', { ns: 'rolex' })}
            </Button>
          </LocalizedLink>
        </TextBlock>
        <RCPOBannerWithTitle
          title={item.elements.third_banner_title.value}
          mobileImage={item.elements.third_banner_mobile_image}
          desktopImage={item.elements.third_banner_desktop_image}
        />
        <KeepExploring
          items={keepExploringItems}
          title={item.elements.keep_exploring_title.value}
        />
      </RCPOPage>
    </Page>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale, preview }) => {
  const lang = locale === 'de' ? 'en' : locale
  try {
    const content = await getPageByCodename<DeletedRolexRcpoCertificationPage>({
      codename: 'rolex_rcpo___certification_page',
      lang: lang as string,
      preview,
    })

    return {
      props: {
        ...content,
        ...(await getServerSideTranslations(locale as string)),
      },
    }
  } catch (error) {
    throw error
  }
}

export default RCPOCertification
